import { ClientResponseFile } from "../components/order-components/DownloadClientResponse";
import {
  Client,
  ClientPrimaryContact,
  HistoryClient,
  SimpleClient,
} from "../interfaces/client";
import { PagedClientsResponse } from "../interfaces/simpleClient";
import { getAdditionalForm } from "./additionalFormService";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertFileToBase64 } from "./helpers";
import {
  getPositionCodes,
  getServiceIDsForPositionCode,
} from "./positionCodeService";
import { getAllServices } from "./serviceService";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getClient = async (clientID: number): Promise<Client> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetClient/${clientID}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client by email");
  }

  return response.json();
};

export const getClientByAbbreviationOrName = async (
  abbreviation: string,
): Promise<Client> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetClientByAbbreviationOrName/${abbreviation}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client by abbreviation");
  }

  return response.json();
};

export const getSimpleClient = async (
  clientID: number,
): Promise<SimpleClient> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Client/GetSimpleClient/${clientID}`,
    {
      method: "GET",
      headers: {},
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client details");
  }

  return response.json();
};

export const getClientPrimaryContact = async (
  clientID: number,
): Promise<ClientPrimaryContact> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Client/getClientPrimaryContact/${clientID}`,
    {
      method: "GET",
      headers: {},
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client primary contact");
  }

  return response.json();
};

export const getSimpleClientByAbbreviation = async (
  abbreviation: string,
): Promise<SimpleClient | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Client/getSimpleClientByAbbreviation/${abbreviation}`,
    {
      method: "GET",
      headers: {},
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client details");
  }
  const text = await response.text();

  if (!text) {
    return null;
  }

  const data: SimpleClient = JSON.parse(text);
  return data;
};

export const getNextAbbreviation = async (): Promise<string> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/getNextAbbreviation`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch next abbreviation");
  }

  return response.text();
};

export const checkAbbreviationExists = async (
  abbreviation: string,
): Promise<boolean> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/checkAbbreviationExists?incomingAbbreviation=${abbreviation}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to check abbreviation.");
  }

  return response.json();
};

export const checkClientStatusByAbbreviation = async (
  abbreviation: string,
): Promise<number> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Client/CheckClientStatusByAbbreviation?abbreviation=${abbreviation}`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client status");
  }

  return response.json();
};

export const getClientByEmail = async (
  clientEmail: string,
  clientID: number,
): Promise<Client> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Client/getClientByEmail/${clientEmail}/${clientID}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch client by email");
    }

    return response.json();
  } catch (error) {
    localStorage.clear();
    window.location.href = "/login";
    throw error;
  }
};

export const createClient = async (newClient: Client): Promise<Client> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(`${apiBaseUrl}/api/Client/CreateNewClient`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newClient),
  });

  if (response.ok) {
    console.log("Client created successfully");
    return response.json();
  } else if (response.status === 409) {
    // Handle conflict (email already in use)
    const errorData = await response.text();
    return Promise.reject(errorData);
  }

  return Promise.reject("An unexpected error occurred.");
};

export const updateClient = async (clientID: number, client: Client) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/Client/UpdateClient/${clientID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(client),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update client");
  }

  return response.status === 204 ? null : await response.json();
};

export const deleteClientBase = async (clientID: number): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/DeleteClientBase/${clientID}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete client. Server responded with status: ${response.status}`,
    );
  }

  console.log(`Client with ID ${clientID} deleted successfully.`);
};

export const checkForms = async (
  clientID: number,
  type?: string,
): Promise<[boolean, string]> => {
  let isSSNTraceSelected = false;
  let isReferenceSelected = false;
  let isCreditReportSelected = false;

  // Fetch position codes and services
  const positionCodes = await getPositionCodes(clientID);
  const allServices = await getAllServices();

  const serviceMap = new Map();
  for (const positionCode of positionCodes) {
    const serviceIDs = await getServiceIDsForPositionCode(
      positionCode.positionCodeID,
    );
    serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
  }

  const signatureForm = await getAdditionalForm(clientID, 1);
  const SSNTraceForm = await getAdditionalForm(clientID, 3);
  const creditReportForm = await getAdditionalForm(clientID, 2);
  const userAgreementForm = await getAdditionalForm(clientID, 4);

  const ssnTraceID = allServices.find(
    (service) => service.serviceID === 17,
  )?.serviceID;
  const creditReportID = allServices.find(
    (service) => service.serviceID === 16,
  )?.serviceID;
  const referenceServiceIDs = allServices
    .filter((service) => service.referenceYears !== null)
    .map((service) => service.serviceID);

  if (ssnTraceID) {
    isSSNTraceSelected = Array.from(serviceMap.values()).some((serviceSet) =>
      serviceSet.has(ssnTraceID),
    );
  }

  if (creditReportID) {
    isCreditReportSelected = Array.from(serviceMap.values()).some(
      (serviceSet) => serviceSet.has(creditReportID),
    );
  }

  if (referenceServiceIDs.length > 0) {
    isReferenceSelected = Array.from(serviceMap.values()).some((serviceSet) =>
      referenceServiceIDs.some((referenceID) => serviceSet.has(referenceID)),
    );
  }

  let returnedMissingString = "";
  let returnedMissing: string[] = [];

  let returnedMessage =
    "Do not process any orders. Client has not completed - ";
  const issues: string[] = [];

  if (isSSNTraceSelected && !SSNTraceForm && !creditReportForm) {
    issues.push("SSN Trace Form");
    returnedMissing.push("3");
  }
  if (isCreditReportSelected && !creditReportForm) {
    issues.push("Credit Report Form");
    returnedMissing.push("2");
  }
  if (isReferenceSelected && !signatureForm) {
    issues.push("Signature Upload");
    returnedMissing.push("1");
  }
  if (!userAgreementForm) {
    issues.push("User Agreement Form");
    returnedMissing.push("4");
  }

  if (type !== "client" && issues.length > 0) {
    returnedMessage += issues.join(", ");
    return [false, returnedMessage];
  } else if (type === "client" && returnedMissing.length > 0) {
    returnedMissingString += returnedMissing.join();
    return [false, returnedMissingString];
  } else {
    return [true, ""];
  }
};

export const checkForAvailablePages = async (
  clientID: number,
): Promise<{
  isReferenceSelected: boolean;
  isSubscriptionSelected: boolean;
}> => {
  let isReferenceSelected = false;
  let isSubscriptionSelected = false;

  // Fetch position codes and services
  const positionCodes = await getPositionCodes(clientID);
  const allServices = await getAllServices();

  const serviceMap = new Map();
  for (const positionCode of positionCodes) {
    const serviceIDs = await getServiceIDsForPositionCode(
      positionCode.positionCodeID,
    );
    serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
  }

  const referenceServiceIDs = allServices
    .filter((service) => service.referenceYears !== null)
    .map((service) => service.serviceID);

  if (referenceServiceIDs.length > 0) {
    isReferenceSelected = Array.from(serviceMap.values()).some((serviceSet) =>
      referenceServiceIDs.some((referenceID) => serviceSet.has(referenceID)),
    );
  }

  const subscriptionServiceIDs = allServices
    .filter((service) => service.subscriptionService === true)
    .map((service) => service.serviceID);

  if (subscriptionServiceIDs.length > 0) {
    isSubscriptionSelected = Array.from(serviceMap.values()).some(
      (serviceSet) =>
        subscriptionServiceIDs.some((subID) => serviceSet.has(subID)),
    );
  }

  return {
    isReferenceSelected,
    isSubscriptionSelected,
  };
};

export const checkEmailExists = async (email: string, clientID: number) => {
  const body = {
    email,
    clientID,
  };

  const response = await fetch(`${apiBaseUrl}/api/Client/CheckEmail`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  return data.exists;
};

export const checkClientInfo = async (
  abbreviation: string,
  positionCode: number,
) => {
  const body = {
    abbreviation,
    positionCode,
  };

  const response = await fetch(`${apiBaseUrl}/api/Client/CheckClientInfo`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  return data.exists;
};

export const printClientProfile = async (
  clientID: number,
  showAbbreviation: boolean,
) => {
  const body = {
    clientID,
    showAbbreviation,
  };

  const response = await fetch(`${apiBaseUrl}/api/Client/PrintClientProfile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return response.blob();
};

export const checkClientHasOrders = async (clientID: number) => {
  const response = await fetch(
    `${apiBaseUrl}/api/Client/CheckClientHasOrders/${clientID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json();
  return data.exists;
};

export const getClientsWithParams = async (
  pageNumber = 1,
  pageSize = 10,
  searchTerm = "",
  clientStatusID = 0,
  sortMode: "updated" | "name" = "updated",
): Promise<PagedClientsResponse> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    searchTerm,
    sortMode,
  });

  if (clientStatusID > 0) {
    params.append("clientStatusID", clientStatusID.toString());
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetClientsWithParams?${params.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch clients");
  }

  const data: PagedClientsResponse = await response.json();
  if (!data || !Array.isArray(data.clients)) {
    throw new Error("Invalid response structure");
  }

  return data;
};

export const getClientsBySearch = async (
  searchTerm = "",
): Promise<SimpleClient[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/SearchClients?searchTerm=${encodeURIComponent(
      searchTerm,
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch clients");
  }

  const data: SimpleClient[] = await response.json();
  return data;
};

export const updateClientStatus = async (
  clientID: number,
  statusID: number,
) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/UpdateClientStatus/${clientID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(statusID),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to update client status");
  }

  return response.status === 204 ? null : await response.json();
};

export const approveClient = async (clientID: number) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const baseUrl = `${siteBaseUrl}?portal=client`;

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/ApproveClient/${clientID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(baseUrl),
    },
  );

  if (response.status === 404) {
    throw new Error("Client not found.");
  }

  if (response.status === 500) {
    throw new Error("An error occurred while approving the client.");
  }

  if (response.status === 207) {
    const data = await response.json();
    return { success: true, warning: data.message };
  }

  if (!response.ok) {
    throw new Error("Failed to approve client.");
  }

  return await response.json();
};

export const downloadMailingWindowClient = async (
  client: Client,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Client/DownloadMailingWindowClient`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(client),
      },
    );

    if (!response.ok) throw new Error("Failed to download Mailing Window");

    return response;
  } catch (error) {
    console.error("Error downloading Mailing Window:", error);
    return null;
  }
};

export const uploadGeneralClientDocument = async (
  client: Client,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      client,
      fileBase64: base64Data,
      contentType,
      fileName: file.name,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Client/UploadGeneralClientDocument`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload client document.");
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const GetAllGeneralClientDocuments = async (
  clientID: number,
): Promise<ClientResponseFile[]> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetAllGeneralClientDocuments?clientID=${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client document files");
  }

  const files: ClientResponseFile[] = await response.json();
  return files;
};

export const GetClientsByEmail = async (email: string): Promise<Client[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetClientsByEmail/${email}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch clients by email");
  }

  return response.json();
};

export const HandleMergeClients = async (
  clientAID: number,
  clientBID: number,
): Promise<{ message: string }> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/MergeClients/${clientAID}/${clientBID}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to merge clients: ${errorText}`);
  }

  return response.json();
};

export const sendAccountCreationConfirmation = async (
  client: Client,
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/Client/SendAccountCreationConfirmationEmail`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(client),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        "Failed to send account creation confirmation email:",
        errorText,
      );
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error sending account creation confirmation email:", error);
    return false;
  }
};

export const softDeleteClient = async (clientID: number) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/SoftDeleteClient/${clientID}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to soft delete client");
  }

  return response.status === 204 ? null : await response.json();
};

export const getClientHistory = async (
  clientID: number,
): Promise<HistoryClient[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/Client/GetClientHistory/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch client history");
  }

  const data: HistoryClient[] = await response.json();
  return data;
};
